import { useCallback } from 'react'
import { useDispatch, useSelector } from 'store'
import { buttonInfo, clickF, codeBoxInfo, inputF, selectF, voidF } from 'types/FunctionTypes'
// import * as PageAction from 'slicers/PageSlice'
import * as ModalUserAction from 'slicers/ModalUserSlice'
import * as ModalAction from 'slicers/ModalSlice'
import { user } from 'types/ReferTypes'
import { postAPI } from 'utils/apiUtil'
import { convertNumber, unDisplayNumber } from 'utils/numberUtil'
import { commonMsg } from 'assets/MessageCode'
import { requestTokuiTen, requestUserAdd, responseTokuiTen } from 'utils/requestUtil'
import { F_NUM, F_STR } from 'assets/FormatNumbers'
import { SingleValue } from 'react-select'
import { select } from 'types/SearchTypes'

/**
 * 得意先コードボックス
 */
export const useTokuiBox = (): codeBoxInfo => {
  const dispatch = useDispatch()
  const loading = useSelector((state) => state.ModalReducer.loading)
  const TOKUI_CD = useSelector((state) => state.ModalUserReducer.TOKUI_CD)
  const TOKUI_NM = useSelector((state) => state.ModalUserReducer.TOKUI_NM)
  const TEN_CD = useSelector((state) => state.ModalUserReducer.TEN_CD)
  const TOKUI_NM_ERROR = useSelector((state) => state.ModalUserReducer.TOKUI_NM_ERROR)

  const changeErrMsg = () => dispatch(ModalAction.changeMessageError(commonMsg.ERROR_MSG)) //
  const loaded = () => dispatch(ModalAction.endLoading()) //

  // 得意先コード入力処理 //================================//
  const changeTokuiCd = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      if (!loading) {
        dispatch(ModalUserAction.changeTokuiCode(convertNumber(e.target.valueAsNumber, 5)))
      }
    },
    [TOKUI_CD, loading],
  )

  // 得意先・店舗コード決定処理 //================================//
  const submitTokui = useCallback(async () => {
    if (TOKUI_CD !== F_NUM && TEN_CD !== F_NUM) {
      if (!loading) {
        dispatch(ModalAction.startLoading('処理中...'))
        await postAPI<requestTokuiTen, responseTokuiTen>(
          '/postTokuiTenName',
          { TOKUI_CD: TOKUI_CD, TEN_CD: TEN_CD },
          (r) => {
            dispatch(ModalAction.changeMessage(r))
            dispatch(ModalUserAction.changeTokuiTen(r))
            dispatch(ModalAction.endLoading())
          },
          changeErrMsg,
          loaded,
        )
      }
    }
  }, [TOKUI_CD, TEN_CD, loading])

  return {
    label: '得意先',
    code: unDisplayNumber(TOKUI_CD),
    name: TOKUI_NM,
    change: changeTokuiCd,
    submit: submitTokui,
    error: TOKUI_NM_ERROR,
  }
}

/**
 * 店舗コードボックス
 */
export const useTenBox = (): codeBoxInfo => {
  const dispatch = useDispatch()
  const loading = useSelector((state) => state.ModalReducer.loading)
  const TEN_CD = useSelector((state) => state.ModalUserReducer.TEN_CD)
  const TEN_NM = useSelector((state) => state.ModalUserReducer.TEN_NM)
  const TOKUI_CD = useSelector((state) => state.ModalUserReducer.TOKUI_CD)
  const TEN_NM_ERROR = useSelector((state) => state.ModalUserReducer.TEN_NM_ERROR)

  const changeErrMsg = () => dispatch(ModalAction.changeMessageError(commonMsg.ERROR_MSG)) //
  const loaded = () => dispatch(ModalAction.endLoading()) //

  // 店舗コード入力処理 //================================//
  const changeTenCd = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      if (!loading) {
        dispatch(ModalUserAction.changeTenCode(convertNumber(e.target.valueAsNumber, 5)))
      }
    },
    [TEN_CD, TOKUI_CD, loading],
  )

  // 店舗コード決定処理 //================================//
  const submitTen = useCallback(async () => {
    if (TOKUI_CD !== F_NUM && TEN_CD !== F_NUM) {
      if (!loading) {
        dispatch(ModalAction.startLoading('処理中...'))
        await postAPI<requestTokuiTen, responseTokuiTen>(
          '/postTokuiTenName',
          { TOKUI_CD: TOKUI_CD, TEN_CD: TEN_CD },
          (r) => {
            dispatch(ModalAction.changeMessage(r))
            dispatch(ModalUserAction.changeTokuiTen(r))
            dispatch(ModalAction.endLoading())
          },
          changeErrMsg,
          loaded,
        )
      }
    }
  }, [TOKUI_CD, TEN_CD, loading])

  return {
    label: '店舗',
    code: unDisplayNumber(TEN_CD),
    name: TEN_NM,
    change: changeTenCd,
    submit: submitTen,
    error: TEN_NM_ERROR,
  }
}

/**
 * メールコードボックス
 */
export const useMailBox = (): codeBoxInfo => {
  const dispatch = useDispatch()
  const loading = useSelector((state) => state.ModalReducer.loading)
  const MAIL = useSelector((state) => state.ModalUserReducer.MAIL)
  const NAME = useSelector((state) => state.ModalUserReducer.NAME)
  const NAME_ERROR = useSelector((state) => state.ModalUserReducer.NAME_ERROR)

  const changeErrMsg = () => dispatch(ModalAction.changeMessageError(commonMsg.ERROR_MSG)) //
  const loaded = () => dispatch(ModalAction.endLoading()) //

  // メールアドレス入力処理 //================================//
  const changeMail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      if (!loading) {
        dispatch(ModalUserAction.changeMail(e.target.value.toLowerCase()))
      }
    },
    [MAIL, loading],
  )

  // メールアドレス決定処理 //================================//
  const submitMail = useCallback(async () => {
    if (!loading) {
      dispatch(ModalAction.startLoading('処理中...'))
      await postAPI<{ MAIL: string }, user>(
        '/postUserNameAdd',
        { MAIL: MAIL },
        (r) => {
          dispatch(ModalAction.changeMessage(r))
          dispatch(ModalUserAction.changeName(r))
          dispatch(ModalAction.endLoading())
        },
        changeErrMsg,
        loaded,
      )
    }
  }, [MAIL])

  return {
    label: 'ﾒｰﾙｱﾄﾞﾚｽ',
    code: MAIL,
    name: NAME,
    change: changeMail,
    submit: submitMail,
    error: NAME_ERROR,
  }
}

// 得意先 //================================//
export const useTokuisaki = (): [string, selectF] => {
  const dispatch = useDispatch()
  const TOKUI_CD = useSelector((state) => state.ModalUserReducer.TOKUI_CD)
  const changeTokui = useCallback(
    (targetValue: SingleValue<any>) => {
      dispatch(ModalUserAction.changeTokuiCode(Number(targetValue!.value)))
      dispatch(ModalUserAction.changeTenCode(F_NUM))
    },
    [TOKUI_CD],
  )
  return [String(TOKUI_CD), changeTokui]
}

// 店舗先 //================================//
export const useTenpo = (): [string, selectF] => {
  const dispatch = useDispatch()
  const TEN_CD = useSelector((state) => state.ModalUserReducer.TEN_CD)
  const changeTokui = useCallback(
    (targetValue: SingleValue<any>) => {
      dispatch(ModalUserAction.changeTenCode(Number(targetValue!.value)))
    },
    [TEN_CD],
  )
  return [String(TEN_CD), changeTokui]
}

// メールアドレス //================================//
export const useMail = (): [string, inputF] => {
  const dispatch = useDispatch()
  const MAIL = useSelector((state) => state.ModalUserReducer.MAIL)
  const changeMail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(ModalUserAction.changeMail(e.target.value))
    },
    [MAIL],
  )
  return [MAIL, changeMail]
}

// 得意先リスト //================================//
export const useTokuisakiList = (): [select[]] => {
  const tokuiList = useSelector((state) => state.ModalUserReducer.tokuiList)

  return [tokuiList]
}

// 店舗リスト //================================//
export const useTenpoList = (): [select[]] => {
  const tenList = useSelector((state) => state.ModalUserReducer.tenList)

  const TOKUI_CD = useSelector((state) => state.ModalUserReducer.TOKUI_CD)

  const editLabel = (list: select[]) => {
    // ラベルの「得意先コード-店舗コード」部分から得意先コードを削除して返す
    return list.map((r) => {
      if (r) {
        let label = r.label
        let splitIndex = label.indexOf('-')
        if (splitIndex > -1) label = label.slice(splitIndex + 1)
        return { ...r, label: label }
      }
      return r
    })
  }
  if (TOKUI_CD === F_NUM) {
    return [editLabel(tenList)]
  } else {
    return [
      editLabel(
        tenList.filter((r) => {
          // ラベルの得意先コードと選択した得意先コードが一致するリストを返す
          if (r) {
            if (r.label.indexOf(TOKUI_CD + '-') > -1) return true
          }
          return false
        }),
      ),
    ]
  }
}

/**
 * ボタン押下処理(ジャペル用追加画面)
 * 追加ボタン
 * 閉じるボタン
 */
export const useButtons = (refetch: voidF): { buttons: buttonInfo[]; buttonClose: buttonInfo } => {
  const dispatch = useDispatch()
  const isJapell = useSelector((state) => state.LoginReducer.isJapell)
  const loading = useSelector((state) => state.ModalReducer.loading)
  const TOKUI_CD = useSelector((state) => state.ModalUserReducer.TOKUI_CD)
  const TEN_CD = useSelector((state) => state.ModalUserReducer.TEN_CD)
  const MAIL = useSelector((state) => state.ModalUserReducer.MAIL)
  const NAME = useSelector((state) => state.ModalUserReducer.NAME)
  const ADD_MAIL = useSelector((state) => state.LoginReducer.MAIL)
  const TOKUI_NM_ERROR = useSelector((state) => state.ModalUserReducer.TOKUI_NM_ERROR)
  const TEN_NM_ERROR = useSelector((state) => state.ModalUserReducer.TEN_NM_ERROR)
  const NAME_ERROR = useSelector((state) => state.ModalUserReducer.NAME_ERROR)
  const request = {
    TOKUI_CD: TOKUI_CD,
    TEN_CD: TEN_CD,
    MAIL: MAIL,
    ADD_MAIL: ADD_MAIL,
  }

  // 追加ボタン押下処理 //================================//
  const clickAdd = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault()
      if (!loading) {
        // ローディング開始
        dispatch(ModalAction.startLoading('処理中...'))
        await postAPI<requestUserAdd, {}>(
          '/postUserAdd',
          request,
          (r) => {
            if (isJapell) {
              dispatch(ModalAction.changeMessage(r))
            } else {
              dispatch(ModalAction.changeMessage(r))
              // if (r.alert) {
              //   dispatch(ModalAction.changeMessage(r))
              // } else {
              //   dispatch(PageAction.changeMessage(r))
              // dispatch(ModalAction.close())
              // dispatch(ModalUserAction.format())
              // refetch()
              // }
            }
          },
          () => dispatch(ModalAction.changeMessageError(commonMsg.ERROR_MSG)),
          () => dispatch(ModalAction.endLoading()),
        )
      }
    },
    [request, isJapell],
  )

  // 閉じるボタン押下処理 //================================//
  const clickClose = useCallback(() => {
    if (!loading) {
      dispatch(ModalAction.close())
      if (!isJapell) dispatch(ModalUserAction.format())
      refetch()
    }
  }, [refetch, isJapell])

  return {
    buttons: [
      {
        disabled: TOKUI_NM_ERROR || TEN_NM_ERROR || NAME_ERROR || TOKUI_CD === F_NUM || TEN_CD === F_NUM || MAIL.trim() === F_STR || (isJapell && NAME === F_STR),
        label: '追加',
        click: clickAdd,
      },
    ],
    buttonClose: {
      disabled: false,
      label: '閉じる',
      click: clickClose,
    },
  }
}

/**
 * ボタン押下処理(得意先用画面)
 * 追加ボタン
 * 閉じるボタン
 */
export const useButtonsTokui = (): [clickF, voidF] => {
  const dispatch = useDispatch()
  const loading = useSelector((state) => state.ModalReducer.loading)
  const TOKUI_CD = useSelector((state) => state.ModalUserReducer.TOKUI_CD)
  const TEN_CD = useSelector((state) => state.ModalUserReducer.TEN_CD)
  const MAIL = useSelector((state) => state.ModalUserReducer.MAIL)
  const request = {
    TOKUI_CD: TOKUI_CD,
    TEN_CD: TEN_CD,
    MAIL: MAIL,
  }

  // 追加ボタン押下処理 //================================//
  const clickInsert = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault()
      if (!loading) {
        // ローディング開始
        dispatch(ModalAction.startLoading('処理中...'))
        await postAPI<requestUserAdd, {}>(
          '/postJokenTokuiDetailInsertBulk',
          request,
          (r) => {
            if (r.error) dispatch(ModalAction.changeMessageError(r.message))
            else {
              dispatch(ModalAction.changeMessageSuccess(r.message))
              dispatch(ModalAction.close())
            }
          },
          () => dispatch(ModalAction.changeMessageError(commonMsg.ERROR_MSG)),
          () => dispatch(ModalAction.endLoading()),
        )
      }
    },
    [request],
  )

  // 閉じるボタン押下処理 //================================//
  const clickClose = useCallback(() => {
    if (!loading) {
      dispatch(ModalAction.close())
      // refetch()
    }
    // }, [refetch])
  }, [])

  return [clickInsert, clickClose]
}
